import * as PATHS from '@/constants/path';
import { request } from '@/services/api/request';
import {
  AutomationSectionPayload,
  EmailTemplateType,
  JourneyEmailConfigModel,
  MailerTransport,
} from '@/typings/api/emailCenter';
import { URLParamBuilder } from '@/utils/functions';

// All journey-scoped endpoints (email-config, email-templates,
// marketing-automations) take the journey HASHID — the raw numeric
// lead_source id is never exposed by the Partner API.

// region Master email templates
export function fetchEmailTemplatesRequest({
  query = {},
}: {
  query?: { type?: EmailTemplateType; page?: number; page_size?: number };
} = {}) {
  const url = URLParamBuilder(PATHS.FETCH_EMAIL_TEMPLATES, {
    queryString: query,
  });
  return request<any>(url, {
    method: 'GET',
  });
}

export function getEmailTemplateRequest({
  params,
}: {
  params: { uuid: string };
}) {
  const url = URLParamBuilder(PATHS.EMAIL_TEMPLATE, { params });
  return request<any>(url, {
    method: 'GET',
  });
}

export function createEmailTemplateRequest({
  payload,
}: {
  payload: Record<string, unknown>;
}) {
  return request<any>(PATHS.FETCH_EMAIL_TEMPLATES, {
    method: 'POST',
    data: payload,
  });
}

export function updateEmailTemplateRequest({
  params,
  payload,
}: {
  params: { uuid: string };
  payload: Record<string, unknown>;
}) {
  const url = URLParamBuilder(PATHS.EMAIL_TEMPLATE, { params });
  return request<any>(url, {
    method: 'PUT',
    data: payload,
  });
}

export function deleteEmailTemplateRequest({
  params,
}: {
  params: { uuid: string };
}) {
  const url = URLParamBuilder(PATHS.EMAIL_TEMPLATE, { params });
  return request<any>(url, {
    method: 'DELETE',
  });
}

/**
 * Detach a shared (master) template: the backend clones it under the current
 * partner, repoints the partner's journey links to the clone and returns the
 * clone. After this the partner no longer receives central master updates.
 */
export function detachEmailTemplateRequest({
  params,
}: {
  params: { uuid: string };
}) {
  const url = URLParamBuilder(PATHS.EMAIL_TEMPLATE_DETACH, { params });
  return request<any>(url, {
    method: 'POST',
  });
}

/**
 * Send the template to an address so it can be checked in a real inbox.
 *
 * `variables` is the preview's own sample data, so the mail matches what the
 * editor is showing rather than the server's guess. `use_draft` sends the
 * unpublished version — the case a draft most needs before it goes out.
 */
export function sendEmailTemplateTestRequest({
  params,
  payload,
}: {
  params: { uuid: string };
  payload: {
    to: string;
    language?: string;
    variables?: Record<string, string>;
    use_draft?: boolean;
  };
}) {
  const url = URLParamBuilder(PATHS.EMAIL_TEMPLATE_TEST, { params });
  return request<any>(url, { method: 'POST', data: payload });
}

/**
 * Send a test message through a mailer's own transport.
 *
 * This is what proves the mailer's connection settings work — a bad host or a
 * rejected credential comes back as a 422 with the transport's own message.
 */
export function sendMailerTestRequest({
  params,
  payload,
}: {
  params: { uuid: string };
  payload: { to: string; from?: string };
}) {
  const url = URLParamBuilder(PATHS.MAILER_TEST, { params });
  return request<any>(url, { method: 'POST', data: payload });
}

/**
 * Upload an image for use inside an email body; resolves to its public URL.
 *
 * `FormData`, so the browser sets the multipart boundary itself — setting
 * Content-Type by hand here produces a boundary-less header the server cannot
 * parse.
 */
export function uploadEmailImageRequest({ file }: { file: File | Blob }) {
  const data = new FormData();
  data.append('file', file);
  return request<any>(PATHS.EMAIL_IMAGES, {
    method: 'POST',
    // Cast: the helper types `data` as a plain record, but a multipart upload
    // is a FormData — the runtime accepts it, the signature just predates it.
    data,
  });
}

// region Template drafts & version history
/**
 * An unpublished edit. 404 when the template has none — that is the normal
 * "nothing staged" answer, not an error.
 */
export function fetchEmailTemplateDraftRequest({
  params,
}: {
  params: { uuid: string };
}) {
  const url = URLParamBuilder(PATHS.EMAIL_TEMPLATE_DRAFT, { params });
  return request<any>(url, { method: 'GET' });
}

/**
 * Create or overwrite the draft. Only the tracked fields are accepted — send a
 * partial and the untouched ones keep their published values.
 */
export function saveEmailTemplateDraftRequest({
  params,
  payload,
}: {
  params: { uuid: string };
  payload: Record<string, unknown>;
}) {
  const url = URLParamBuilder(PATHS.EMAIL_TEMPLATE_DRAFT, { params });
  return request<any>(url, { method: 'PUT', data: payload });
}

/** Throw the draft away; the template keeps what it was already sending. */
export function discardEmailTemplateDraftRequest({
  params,
}: {
  params: { uuid: string };
}) {
  const url = URLParamBuilder(PATHS.EMAIL_TEMPLATE_DRAFT, { params });
  return request<any>(url, { method: 'DELETE' });
}

/** Copy the draft onto the template — this is the moment it goes live. */
export function publishEmailTemplateDraftRequest({
  params,
}: {
  params: { uuid: string };
}) {
  const url = URLParamBuilder(PATHS.EMAIL_TEMPLATE_DRAFT_PUBLISH, { params });
  return request<any>(url, { method: 'POST' });
}

/**
 * Version list, newest first. Each entry holds the values that were replaced,
 * so restoring one returns the template to that state. Bodies are omitted from
 * the list (`body_size` stands in for them).
 */
export function fetchEmailTemplateHistoryRequest({
  params,
}: {
  params: { uuid: string };
}) {
  const url = URLParamBuilder(PATHS.EMAIL_TEMPLATE_HISTORY, { params });
  return request<any>(url, { method: 'GET' });
}

/** Restore a version. Non-destructive — the current values are snapshotted first. */
export function restoreEmailTemplateVersionRequest({
  params,
}: {
  params: { uuid: string; historyId: number };
}) {
  const url = URLParamBuilder(PATHS.EMAIL_TEMPLATE_HISTORY_RESTORE, { params });
  return request<any>(url, { method: 'POST' });
}
// endregion

export function fetchEmailTemplateTypesRequest() {
  return request<any>(PATHS.FETCH_EMAIL_TEMPLATE_TYPES, {
    method: 'GET',
  });
}

export function fetchEmailVariablesRequest() {
  return request<any>(PATHS.FETCH_EMAIL_VARIABLES, {
    method: 'GET',
  });
}

/**
 * Recent leads or appointments a preview can be populated from, newest first.
 *
 * `search` narrows by customer name or email; the server caps `limit` at 50.
 */
export function fetchEmailPreviewSamplesRequest({
  query,
}: {
  query: { type: 'appointment' | 'lead'; search?: string; limit?: number };
}) {
  const url = URLParamBuilder(PATHS.EMAIL_PREVIEW_SAMPLES, {
    queryString: query,
  });
  return request<any>(url, { method: 'GET' });
}

/**
 * One record's **send-time** variables as the preview's flat dot-path map —
 * built by the same code a real send uses, so what the preview shows is what
 * the customer got. `language` picks the locale its dates are formatted in.
 */
export function fetchEmailPreviewSampleRequest({
  params,
  query = {},
}: {
  params: { type: 'appointment' | 'lead'; id: string };
  query?: { language?: string };
}) {
  const url = URLParamBuilder(PATHS.EMAIL_PREVIEW_SAMPLE, {
    params,
    queryString: query,
  });
  return request<any>(url, { method: 'GET' });
}
// endregion

// region Journey email config (journeyId = HASHID)
export function fetchJourneyEmailConfigRequest({
  journeyHashId,
}: {
  journeyHashId: string;
}) {
  const url = URLParamBuilder(PATHS.JOURNEY_EMAIL_CONFIG, {
    params: { journeyId: journeyHashId },
  });
  return request<any>(url, {
    method: 'GET',
  });
}

/** PATCH = deep merge into the stored config (the drawer default). */
export function patchJourneyEmailConfigRequest({
  journeyHashId,
  payload,
}: {
  journeyHashId: string;
  payload: JourneyEmailConfigModel;
}) {
  const url = URLParamBuilder(PATHS.JOURNEY_EMAIL_CONFIG, {
    params: { journeyId: journeyHashId },
  });
  return request<any>(url, {
    method: 'PATCH',
    data: payload,
  });
}

/**
 * Switch the journey's emails off: an **empty** `email_config` row of its own.
 *
 * Not a PUT of `{}` — the endpoint requires a sender on PUT, and PATCH merges,
 * so neither verb can clear a stored config. The way back is
 * `deleteJourneyConfigurationRequest('email_config')` (follow the partner
 * default again) or a PUT with a sender of the journey's own.
 */
export function deactivateJourneyEmailConfigRequest({
  journeyHashId,
}: {
  journeyHashId: string;
}) {
  const url = URLParamBuilder(PATHS.JOURNEY_EMAIL_CONFIG_DEACTIVATE, {
    params: { journeyId: journeyHashId },
  });
  return request<any>(url, { method: 'POST' });
}

/** PUT = full replace of the stored config. */
export function putJourneyEmailConfigRequest({
  journeyHashId,
  payload,
}: {
  journeyHashId: string;
  payload: JourneyEmailConfigModel;
}) {
  const url = URLParamBuilder(PATHS.JOURNEY_EMAIL_CONFIG, {
    params: { journeyId: journeyHashId },
  });
  return request<any>(url, {
    method: 'PUT',
    data: payload,
  });
}
// endregion

// region Journey email templates (journeyId = HASHID)
export function fetchJourneyEmailTemplatesRequest({
  journeyHashId,
}: {
  journeyHashId: string;
}) {
  const url = URLParamBuilder(PATHS.JOURNEY_EMAIL_TEMPLATES, {
    params: { journeyId: journeyHashId },
  });
  return request<any>(url, {
    method: 'GET',
  });
}

export function linkJourneyEmailTemplateRequest({
  journeyHashId,
  payload,
}: {
  journeyHashId: string;
  payload: Record<string, unknown>; // template_id, email_type, language?, bcc?, reply_to?, is_enabled?
}) {
  const url = URLParamBuilder(PATHS.JOURNEY_EMAIL_TEMPLATES, {
    params: { journeyId: journeyHashId },
  });
  return request<any>(url, {
    method: 'POST',
    data: payload,
  });
}

export function updateJourneyEmailTemplateRequest({
  journeyHashId,
  emailType,
  payload,
}: {
  journeyHashId: string;
  emailType: string;
  payload: Record<string, unknown>;
}) {
  const url = URLParamBuilder(PATHS.JOURNEY_EMAIL_TEMPLATE, {
    params: { journeyId: journeyHashId, emailType },
  });
  return request<any>(url, {
    method: 'PUT',
    data: payload,
  });
}

/**
 * `delete_template` also deletes the linked master template — only when the
 * partner owns it, and 409s (deleting nothing) while any other journey link
 * or automation still uses it. `language` targets the exact per-language link
 * ('' = the catch-all row); without it the backend unlinks the first match.
 */
export function unlinkJourneyEmailTemplateRequest({
  journeyHashId,
  emailType,
  query,
}: {
  journeyHashId: string;
  emailType: string;
  query?: { delete_template?: boolean; language?: string };
}) {
  const url = URLParamBuilder(PATHS.JOURNEY_EMAIL_TEMPLATE, {
    params: { journeyId: journeyHashId, emailType },
    queryString: query,
  });
  return request<any>(url, {
    method: 'DELETE',
  });
}

/**
 * Switch one lead source's Newsletter2Go campaign mail on or off.
 *
 * The NL2Go equivalent of a journey link's `is_enabled`: it takes the campaign's
 * trigger groups off this journey's newsletter over there, so the mail keeps its
 * content and schedule and has nobody left to send to. 502 means NL2Go refused
 * and nothing was written — the row still describes what is actually sending.
 */
export function updateJourneyCampaignEmailRequest({
  journeyHashId,
  templateId,
  isEnabled,
}: {
  journeyHashId: string;
  templateId: string;
  isEnabled: boolean;
}) {
  const url = URLParamBuilder(PATHS.JOURNEY_CAMPAIGN_EMAIL, {
    params: { journeyId: journeyHashId, templateId },
  });
  return request<any>(url, {
    method: 'PUT',
    data: { is_enabled: isEnabled },
  });
}

/**
 * Take a Newsletter2Go campaign mail off one lead source.
 *
 * Deletes this journey's copy of the mail at NL2Go and the campaign triggers'
 * rows for it. `delete_template` additionally deletes the template — but only
 * when the partner owns it and nothing else still sends it; a shared master, or
 * one another journey still uses, simply stays. Unlike the transactional unlink
 * this never 409s: removing the mail from *this* journey always succeeds.
 */
export function deleteJourneyCampaignEmailRequest({
  journeyHashId,
  templateId,
  query,
}: {
  journeyHashId: string;
  templateId: string;
  query?: { delete_template?: boolean };
}) {
  const url = URLParamBuilder(PATHS.JOURNEY_CAMPAIGN_EMAIL, {
    params: { journeyId: journeyHashId, templateId },
    queryString: query,
  });
  return request<any>(url, {
    method: 'DELETE',
  });
}

/**
 * ⚠️ Known backend bug: this endpoint 500s on the SUCCESS path (the mail is
 * sent before the response fails). Interpret results with
 * interpretTestSendResult from @/utils/apiErrors — never treat a plain 500
 * from here as a definite failure.
 */
export function testJourneyEmailTemplateRequest({
  journeyHashId,
  emailType,
  payload,
}: {
  journeyHashId: string;
  emailType: string;
  payload: { to: string; lead_id?: number | string };
}) {
  const url = URLParamBuilder(PATHS.JOURNEY_EMAIL_TEMPLATE_TEST, {
    params: { journeyId: journeyHashId, emailType },
  });
  return request<any>(url, {
    method: 'POST',
    data: payload,
  });
}
// endregion

// region Marketing automations (journeyId = HASHID)
export function fetchMarketingAutomationsRequest({
  journeyHashId,
}: {
  journeyHashId: string;
}) {
  const url = URLParamBuilder(PATHS.JOURNEY_MARKETING_AUTOMATIONS, {
    params: { journeyId: journeyHashId },
  });
  return request<any>(url, {
    method: 'GET',
  });
}

export function createMarketingAutomationRequest({
  journeyHashId,
  payload,
}: {
  journeyHashId: string;
  payload: { name: string; sections: AutomationSectionPayload[] };
}) {
  const url = URLParamBuilder(PATHS.JOURNEY_MARKETING_AUTOMATIONS, {
    params: { journeyId: journeyHashId },
  });
  return request<any>(url, {
    method: 'POST',
    data: payload,
  });
}

/** PUT sections = full replace on the backend. */
export function updateMarketingAutomationRequest({
  journeyHashId,
  automationId,
  payload,
}: {
  journeyHashId: string;
  automationId: string; // hash_id
  payload: { name: string; sections: AutomationSectionPayload[] };
}) {
  const url = URLParamBuilder(PATHS.JOURNEY_MARKETING_AUTOMATION, {
    params: { journeyId: journeyHashId, automationId },
  });
  return request<any>(url, {
    method: 'PUT',
    data: payload,
  });
}

export function deleteMarketingAutomationRequest({
  journeyHashId,
  automationId,
}: {
  journeyHashId: string;
  automationId: string; // hash_id
}) {
  const url = URLParamBuilder(PATHS.JOURNEY_MARKETING_AUTOMATION, {
    params: { journeyId: journeyHashId, automationId },
  });
  return request<any>(url, {
    method: 'DELETE',
  });
}
// endregion

// region Template fragments
/** Layouts + partials for client-side Twig preview rendering; not paginated. */
export function fetchTemplateFragmentsRequest() {
  return request<any>(PATHS.TEMPLATE_FRAGMENTS, {
    method: 'GET',
  });
}

/**
 * Editor labels the `core/*` components declare in their manifests, resolved
 * server-side for one locale (`{ locale, labels }`, keys fully qualified). A
 * separate endpoint rather than a field on /template-fragments because the map
 * is one per request, not one per fragment.
 */
export function fetchEmailComponentLabelsRequest({
  params,
}: {
  params?: { locale?: string };
} = {}) {
  return request<any>(PATHS.EMAIL_COMPONENT_LABELS, {
    method: 'GET',
    params: params?.locale ? { locale: params.locale } : undefined,
  });
}

// The reference key is never sent — the server assigns a fresh one, so a copy
// of a standard block is a new block rather than a replacement for it.
export function createTemplateFragmentRequest({
  payload,
}: {
  payload: {
    name: string;
    fragment_type: string;
    content: string;
  };
}) {
  return request<any>(PATHS.TEMPLATE_FRAGMENTS, {
    method: 'POST',
    data: payload,
  });
}

export function updateTemplateFragmentRequest({
  params,
  payload,
}: {
  params: { fragmentId: number };
  // The reference key is immutable; renaming means `name`.
  payload: { name?: string; fragment_type?: string; content?: string };
}) {
  const url = URLParamBuilder(PATHS.TEMPLATE_FRAGMENT, { params });
  return request<any>(url, {
    method: 'PUT',
    data: payload,
  });
}

/**
 * What still references the fragment, and whether that blocks deleting it.
 * Run before offering the delete — DELETE answers 409 for the same case, but
 * finding out by pressing the button is exactly what this avoids.
 */
export function fetchTemplateFragmentUsageRequest({
  params,
}: {
  params: { fragmentId: number };
}) {
  const url = URLParamBuilder(PATHS.TEMPLATE_FRAGMENT_USAGE, { params });
  return request<any>(url, {
    method: 'GET',
  });
}

export function deleteTemplateFragmentRequest({
  params,
}: {
  params: { fragmentId: number };
}) {
  const url = URLParamBuilder(PATHS.TEMPLATE_FRAGMENT, { params });
  return request<any>(url, {
    method: 'DELETE',
  });
}
// endregion

// region Email settings
export function fetchEmailSettingsRequest() {
  return request<any>(PATHS.EMAIL_SETTINGS, {
    method: 'GET',
  });
}

export function updateEmailSettingsRequest({
  payload,
}: {
  payload: { default_layout_key: string | null };
}) {
  return request<any>(PATHS.EMAIL_SETTINGS, {
    method: 'PUT',
    data: payload,
  });
}
// endregion

// region Partner languages
export function fetchPartnerLanguagesRequest() {
  return request<any>(PATHS.PARTNER_LANGUAGES, {
    method: 'GET',
  });
}

export function updatePartnerLanguagesRequest({
  payload,
}: {
  payload: { languages: string[] };
}) {
  return request<any>(PATHS.PARTNER_LANGUAGES, {
    method: 'PUT',
    data: payload,
  });
}
// endregion

// region Partner translations
export function fetchPartnerTranslationsRequest() {
  return request<any>(PATHS.PARTNER_TRANSLATIONS, {
    method: 'GET',
  });
}

export function createPartnerTranslationRequest({
  payload,
}: {
  payload: {
    translation_group: string;
    key: string;
    value: Record<string, string>;
  };
}) {
  return request<any>(PATHS.PARTNER_TRANSLATIONS, {
    method: 'POST',
    data: payload,
  });
}

export function updatePartnerTranslationRequest({
  params,
  payload,
}: {
  params: { uuid: string };
  payload: {
    translation_group?: string;
    key?: string;
    value?: Record<string, string>;
  };
}) {
  const url = URLParamBuilder(PATHS.PARTNER_TRANSLATION, { params });
  return request<any>(url, {
    method: 'PUT',
    data: payload,
  });
}

export function deletePartnerTranslationRequest({
  params,
}: {
  params: { uuid: string };
}) {
  const url = URLParamBuilder(PATHS.PARTNER_TRANSLATION, { params });
  return request<any>(url, {
    method: 'DELETE',
  });
}
// endregion

// region Mailers
export function fetchMailersRequest() {
  return request<any>(PATHS.MAILERS, {
    method: 'GET',
  });
}

export function createMailerRequest({
  payload,
}: {
  payload: {
    name: string;
    transport: MailerTransport;
    config?: Record<string, unknown>;
  };
}) {
  return request<any>(PATHS.MAILERS, {
    method: 'POST',
    data: payload,
  });
}

export function updateMailerRequest({
  params,
  payload,
}: {
  params: { uuid: string };
  payload: Record<string, unknown>;
}) {
  const url = URLParamBuilder(PATHS.MAILER, { params });
  return request<any>(url, {
    method: 'PUT',
    data: payload,
  });
}

export function deleteMailerRequest({ params }: { params: { uuid: string } }) {
  const url = URLParamBuilder(PATHS.MAILER, { params });
  return request<any>(url, {
    method: 'DELETE',
  });
}

/**
 * Which mailer a journey's mail leaves through.
 *
 * Journey-scoped rather than one partner-wide default, because the mailer is
 * stored on the Mailcoach mailing list the journey sends to — that is what
 * Mailcoach reads when it sends. `has_mailing_list: false` means the journey
 * has no list configured yet, so there is nothing to attach a mailer to.
 */
export function fetchJourneyMailerRequest({
  params,
}: {
  params: { journeyId: string };
}) {
  const url = URLParamBuilder(PATHS.JOURNEY_MAILER, { params });
  return request<any>(url, {
    method: 'GET',
  });
}

/**
 * Set one or both channels. A field left out is untouched; sending it as null
 * hands that channel back to the Mailcoach default.
 */
export function setJourneyMailerRequest({
  params,
  payload,
}: {
  params: { journeyId: string };
  payload: {
    transactional_mailer_id?: string | null;
    automation_mailer_id?: string | null;
  };
}) {
  const url = URLParamBuilder(PATHS.JOURNEY_MAILER, { params });
  return request<any>(url, {
    method: 'PUT',
    data: payload,
  });
}
// endregion
